/**
 * Debug Logging System für Frontend
 * 
 * Verwendung:
 * - debug.log("Component", "Message", data) - Normale Debug-Info (nur wenn aktiviert)
 * - debug.warn("Component", "Warning", data) - Warnung (immer)
 * - debug.error("Component", "Error", data) - Fehler (immer)
 * 
 * Aktivierung/Deaktivierung:
 * - Im Code: setDebugEnabled(true/false)
 * - In Browser Console: localStorage.setItem('debug', 'true')
 * - In Browser Console: localStorage.setItem('debug', 'false')
 */

// Check if debug is enabled via localStorage or environment
let debugEnabled =
    localStorage.getItem('debug') === 'true' ||
    import.meta.env.DEV === true; // Always enabled in development

export function setDebugEnabled(enabled: boolean): void {
    debugEnabled = enabled;
    localStorage.setItem('debug', enabled ? 'true' : 'false');
    console.log(`[Debug] Logging ${enabled ? 'ENABLED' : 'DISABLED'}`);
}

export function isDebugEnabled(): boolean {
    return debugEnabled;
}

export const debug = {
    log: (component: string, message: string, data?: any) => {
        if (!debugEnabled) return;
        const timestamp = new Date().toISOString().substring(11, 23);
        if (data !== undefined) {
            console.log(`[${timestamp}] [${component}] ${message}`, data);
        } else {
            console.log(`[${timestamp}] [${component}] ${message}`);
        }
    },

    warn: (component: string, message: string, data?: any) => {
        const timestamp = new Date().toISOString().substring(11, 23);
        if (data !== undefined) {
            console.warn(`[${timestamp}] [${component}] ⚠️ ${message}`, data);
        } else {
            console.warn(`[${timestamp}] [${component}] ⚠️ ${message}`);
        }
    },

    error: (component: string, message: string, data?: any) => {
        const timestamp = new Date().toISOString().substring(11, 23);
        if (data !== undefined) {
            console.error(`[${timestamp}] [${component}] ❌ ${message}`, data);
        } else {
            console.error(`[${timestamp}] [${component}] ❌ ${message}`);
        }
    },

    group: (component: string, title: string) => {
        if (!debugEnabled) return;
        console.group(`[${component}] ${title}`);
    },

    groupEnd: () => {
        if (!debugEnabled) return;
        console.groupEnd();
    }
};

// Make debug control available in browser console
(window as any).debugControl = {
    enable: () => setDebugEnabled(true),
    disable: () => setDebugEnabled(false),
    status: () => console.log(`Debug logging is ${debugEnabled ? 'ENABLED' : 'DISABLED'}`),
};

// Log initial state
if (debugEnabled) {
    console.log('[Debug] 🔍 Debug logging is ENABLED');
    console.log('[Debug] 💡 To disable: debugControl.disable() or localStorage.setItem("debug", "false")');
} else {
    console.log('[Debug] Debug logging is DISABLED');
    console.log('[Debug] 💡 To enable: debugControl.enable() or localStorage.setItem("debug", "true")');
}
